import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile } from '../services/profileService';
import { getMemberLabData, getHistory } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import Hba1cFormula from '../formula/Hba1c';

const HbA1cContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#4CAF50'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HbA1cIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 4px 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #4CAF50;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const HbA1cPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hba1cData, setHba1cData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [latestValues, setLatestValues] = useState({
    hba1c: { value: '-', status: 'ปกติ', color: '#4CAF50' }
  });
  const [profileData, setProfileData] = useState({ age: 0 });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    hba1c: ''
  });

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      hba1c: record.hba1c || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      console.log('📝 Updating HbA1c data:', editingRecord.date, editForm);

      if (editForm.hba1c && editForm.hba1c !== editingRecord.hba1c) {
        await updateLabData(editingRecord.date, 'hba1c', editForm.hba1c);
      }

      console.log('✅ HbA1c data updated successfully');
      alert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadHbA1cData();
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('❌ Error updating HbA1c data:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const [datePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${(parseInt(year) + 543).toString().slice(-2)}`;
  };

  const calculateRisk = (hba1c) => {
    const hba1cVal = parseFloat(hba1c) || 0;
    if (!hba1c) return '-';

    if (hba1cVal < 5.7) return 'ปกติ';
    if (hba1cVal >= 5.7 && hba1cVal < 6.5) return 'เสี่ยง';
    return 'ผิดปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const datePart = dateString.split(' ')[0];
    const [year, month, day] = datePart.split('-');
    const thaiYear = parseInt(year) + 543;
    const thaiMonth = thaiMonths[parseInt(month) - 1];

    return `${parseInt(day)} ${thaiMonth} ${thaiYear}`;
  };

  const loadHbA1cData = async () => {
    try {
      setLoading(true);
      console.log('🩺 HbA1c - Loading data...');

      // ====== ดึงข้อมูลจาก MFU API (เส้นเดียวกับ mfu-admin) ======
      let memberLabData = null;
      let age = 0;
      let gender = 'M';

      try {
        memberLabData = await getMemberLabData();
        console.log('📊 Member Lab Data:', memberLabData);

        if (memberLabData?.profile) {
          age = memberLabData.profile.age || 0;
          gender = memberLabData.profile.gender || 'M';
        }
      } catch (memberLabError) {
        console.warn("⚠️ Could not load member lab data:", memberLabError);
      }

      // Fallback: ถ้าไม่ได้ profile จาก memberLabData
      if (!age) {
        const profileResponse = await getProfile();
        const profile = profileResponse?.data || profileResponse?.profile || {};
        age = profile.age || 0;
        gender = profile.sex || 'M';
      }

      setProfileData({ age, gender });

      const labData = memberLabData?.labData || {};

      // ดึงข้อมูลจาก memberLabData หรือ fallback ไป API เดิม
      let hba1cArray = [];

      const hba1cHistory = getHistory(labData, 'hba1c');
      if (hba1cHistory.length > 0) {
        hba1cArray = hba1cHistory.map(item => ({ dateadd: item.date, value: item.value }));
      } else {
        const hba1cResponse = await getLabDataByName('hba1c');
        hba1cArray = hba1cResponse?.data || [];
      }

      console.log('📊 HbA1c Data:', { HbA1c: hba1cArray.length });
      console.log('📊 HbA1c Raw Data:', hba1cArray);

      const recordsMap = new Map();
      hba1cArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hba1c: null });
        }
        recordsMap.get(date).hba1c = item.value;
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      setAllRecords(combinedRecords);

      const processedHbA1c = hba1cArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      setHba1cData(processedHbA1c);

      if (hba1cArray.length > 0) {
        const latestHbA1c = hba1cArray[hba1cArray.length - 1];
        setLatestDate(formatThaiDate(latestHbA1c.dateadd));

        // Calculate HbA1c status using formula
        const hba1cValue = parseFloat(latestHbA1c?.value || 0);
        const hba1cResult = latestHbA1c ? await Hba1cFormula(hba1cValue, age, gender) : null;
        const hba1cColorMap = { 'green': '#4CAF50', 'red': '#ff4757', 'gray': '#e0e0e0' };

        setLatestValues({
          hba1c: {
            value: `${hba1cValue.toFixed(1)} %`,
            status: hba1cResult?.title || 'ปกติ',
            color: hba1cResult ? (hba1cColorMap[hba1cResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: hba1cValue
          }
        });
      }

      console.log('✅ HbA1c - Data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading HbA1c data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHbA1cData();
  }, []);

  return (
    <HbA1cContainer>
      <MainHeader />

      <TopBar>น้ำตาลสะสม</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>น้ำตาลสะสม (HbA1c)</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check if HbA1c is abnormal (not green)
              const isAbnormal = latestValues.hba1c.value !== '-' && latestValues.hba1c.color !== '#4CAF50';

              return (
                <StatusCard $bgColor={latestValues.hba1c.color}>
                  <HbA1cIcon src="./น้ำตาลสี.png" alt="น้ำตาลสะสม" />
                  <StatusInfo>
                    {!isAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        <AbnormalName>HbA1c</AbnormalName>
                        <AbnormalValue>{latestValues.hba1c.value}</AbnormalValue>
                        <AbnormalStatus>{latestValues.hba1c.status}</AbnormalStatus>
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            <ChartSection>
              <ChartTitle>HbA1c</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (&lt; 5.7%)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFF3E0" />
                  <span>เสี่ยง (5.7-6.4%)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (≥ 6.5%)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={hba1cData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 10]}
                    ticks={[0, 2, 4, 5.7, 6.5, 8, 10]}
                    tick={{ fontSize: 12 }}
                  />
                  <ReferenceArea y1={0} y2={5.7} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={5.7} y2={6.5} fill="#FFF3E0" fillOpacity={0.6} />
                  <ReferenceArea y1={6.5} y2={10} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDisplayDate(record.date)}</TableCell>
                        <TableCell>{record.hba1c ? `${record.hba1c} %` : '-'}</TableCell>
                        <TableCell>{calculateRisk(record.hba1c)}</TableCell>
                        <TableCell>
                          <EditButton onClick={() => handleEditClick(record)}>
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </EditButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>HbA1c (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.hba1c}
                onChange={(e) => setEditForm({ ...editForm, hba1c: e.target.value })}
                placeholder="&lt; 5.7 ปกติ, 5.7-6.4 เสี่ยง, ≥ 6.5 สูง"
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </HbA1cContainer>
  );
};

export default HbA1cPage;
